/**
 * User entity model class
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import BaseModel from './BaseModel';
import BaseCollection from './BaseCollection';
import dayjs from 'dayjs';
import { getI18nInstance as i18n } from '@/i18n';
import { userIsAdmin, userIsStaff, userIsOrganizer } from '@/helpers/auth';

export const ROLES = {
    ADMIN: 'ADMIN',
    EDITOR: 'EDITOR',
    WRITER: 'WRITER',
    EVENT_ORGANIZER: 'EVENT_ORGANIZER',
    MANAGER: 'MANAGER',
} as { [key: string]: string };

export class User extends BaseModel {
    defaults() {
        return {
            id: null,
            active: false,
            created: null,
            modified: null,
            username: null,
            last_login: null,
            login_count: null,
            failed_logins: null,
            firstname: null,
            lastname: null,
            locale: null,
            email: null,
            initials: null,
            role: null,
            event_organizer_id: null,
            organizer_group_id: null,
            change_flag: null,
            is_writer: null,
            is_admin: null,
            is_editor: null,
            is_event_organizer: null,
            infomail_tgk: null,
            infomail_tgk_date: null,
            password_change: null,
            ak_module_active: false,

            // view fields
            title: null,
            organizer_group: null,
            organizer_group_description: null,

            // virtual fields
            event_organizer: null,
        };
    }

    mutations() {
        return {
            id: (id: string | number | null) => (Number(id) > 0 ? Number(id) : null),
            active: Boolean,
            change_flag: Boolean,
            infomail_tgk: Boolean,
            login_count: Number,
            ak_module_active: Boolean,
            created: dayjs,
            modified: dayjs,
            last_login: dayjs,
            infomail_tgk_date: dayjs,
        };
    }

    entityName() {
        return 'User';
    }

    get isAdmin() {
        return userIsAdmin();
    }

    get isStaff() {
        return userIsStaff();
    }

    get isOrganizer() {
        return userIsOrganizer();
    }

    get isSingleOrganizer() {
        return userIsOrganizer() && this.organizer_group_id <= 0;
    }

    get roleName() {
        return i18n().t(this?.role?.toLowerCase());
    }

    get fullname() {
        return [this?.firstname, this?.lastname].filter(Boolean).join(' ');
    }

    options() {
        const opts = super.options();
        Object.assign(opts.methods, { saveProfile: 'POST' });
        return opts;
    }

    routes() {
        const className = this.entityName();
        const routes = super.routes();
        Object.assign(routes, { saveProfile: `/${className}/saveProfile` });
        return routes;
    }

    saveProfile() {
        const method = this.getOption('methods.saveProfile');
        const route = this.getRoute('saveProfile');
        const params = this.getRouteParameters();
        const url = this.getURL(route, params);

        // Profile save: only some few properties are allowed to be stored:
        const data = {
            password_change: this.password_change,
            firstname: this.firstname,
            lastname: this.lastname,
            initials: this.initials,
            locale: this.locale,
            email: this.email,
            infomail_tgk: this.infomail_tgk,
        };

        return this.createRequest({ method, url, data }).send();
    }
}

export class UserCollection extends BaseCollection {
    options() {
        return {
            model: User,
        };
    }

    entityName() {
        return 'User';
    }
}
